import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserMenu } from "@/components/UserMenu";
import {
  Star,
  CheckCircle,
  Warning,
  ChartBar,
  BookOpen,
  DownloadSimple,
  LockSimple,
} from "@phosphor-icons/react";

const COPY = {
  en: {
    eyebrow: "Free Limited Trial",
    title: "Your interview feedback preview",
    intro: "Here is the complete feedback for your first interview question so you can experience how the Interview Simulator evaluates and coaches your answers.",
    question: "Question",
    overall: "Overall",
    performance: "Interview Performance",
    english: "English Language",
    transcript: "Your Transcript",
    suggested: "Suggested Answer",
    strengths: "Strengths",
    improve: "How to Improve",
    noStrengths: "No specific strengths were identified in this response.",
    noImprovements: "No specific improvement points were identified in this response.",
    relevance: "Relevance",
    evidence: "Supporting Evidence",
    organization: "Organization",
    competency: "Professional Competency",
    results: "Evidence of Results",
    presence: "Professional Presence",
    grammar: "Grammar",
    vocabulary: "Vocabulary",
    clarity: "Clarity",
    tone: "Professional Tone",
    fluency: "Fluency",
    insufficient: "There was not enough spoken English in this answer to assess the English-language dimensions reliably.",
    locked: "Sample report view. To see a full interview report and study guide, sign up for a paid account.",
    report: "Download Interview Report",
    guide: "Download Study Guide",
    paidOnly: "Available with a paid account",
    home: "Return to account home",
  },
  "pt-BR": {
    eyebrow: "Teste Gratuito Limitado",
    title: "Prévia do feedback da sua entrevista",
    intro: "Veja o feedback completo da sua primeira pergunta para conhecer como o Interview Simulator avalia e orienta suas respostas.",
    question: "Pergunta",
    overall: "Geral",
    performance: "Desempenho na Entrevista",
    english: "Língua Inglesa",
    transcript: "Sua Transcrição",
    suggested: "Resposta Sugerida",
    strengths: "Pontos Fortes",
    improve: "Como Melhorar",
    noStrengths: "Nenhum ponto forte específico foi identificado nesta resposta.",
    noImprovements: "Nenhum ponto específico de melhoria foi identificado nesta resposta.",
    relevance: "Relevância",
    evidence: "Evidências de Apoio",
    organization: "Organização",
    competency: "Competência Profissional",
    results: "Evidência de Resultados",
    presence: "Presença Profissional",
    grammar: "Gramática",
    vocabulary: "Vocabulário",
    clarity: "Clareza",
    tone: "Tom Profissional",
    fluency: "Fluência",
    insufficient: "Não houve inglês falado suficiente nesta resposta para avaliar com segurança os critérios de idioma.",
    locked: "Visualização de exemplo do relatório. Para ver o relatório completo da entrevista e o guia de estudos, assine uma conta paga.",
    report: "Baixar Relatório da Entrevista",
    guide: "Baixar Guia de Estudos",
    paidOnly: "Disponível com uma conta paga",
    home: "Voltar à página inicial da conta",
  },
  "es-419": {
    eyebrow: "Prueba Gratuita Limitada",
    title: "Vista previa de los comentarios de su entrevista",
    intro: "Aquí puede ver los comentarios completos de su primera pregunta para conocer cómo Interview Simulator evalúa y orienta sus respuestas.",
    question: "Pregunta",
    overall: "General",
    performance: "Desempeño en la Entrevista",
    english: "Idioma Inglés",
    transcript: "Su Transcripción",
    suggested: "Respuesta Sugerida",
    strengths: "Fortalezas",
    improve: "Cómo Mejorar",
    noStrengths: "No se identificaron fortalezas específicas en esta respuesta.",
    noImprovements: "No se identificaron puntos específicos de mejora en esta respuesta.",
    relevance: "Relevancia",
    evidence: "Evidencia de Apoyo",
    organization: "Organización",
    competency: "Competencia Profesional",
    results: "Evidencia de Resultados",
    presence: "Presencia Profesional",
    grammar: "Gramática",
    vocabulary: "Vocabulario",
    clarity: "Claridad",
    tone: "Tono Profesional",
    fluency: "Fluidez",
    insufficient: "No hubo suficiente inglés hablado en esta respuesta para evaluar de forma confiable las dimensiones del idioma.",
    locked: "Vista de ejemplo del informe. Para ver el informe completo de la entrevista y la guía de estudio, regístrese para obtener una cuenta de pago.",
    report: "Descargar Informe de Entrevista",
    guide: "Descargar Guía de Estudio",
    paidOnly: "Disponible con una cuenta de pago",
    home: "Volver a la página principal de la cuenta",
  },
  "fr-CA": {
    eyebrow: "Essai Gratuit Limité",
    title: "Aperçu de la rétroaction de votre entrevue",
    intro: "Voici la rétroaction complète pour votre première question afin de vous montrer comment Interview Simulator évalue et améliore vos réponses.",
    question: "Question",
    overall: "Global",
    performance: "Performance en Entrevue",
    english: "Langue Anglaise",
    transcript: "Votre Transcription",
    suggested: "Réponse Suggérée",
    strengths: "Points Forts",
    improve: "Comment S'améliorer",
    noStrengths: "Aucun point fort précis n’a été relevé dans cette réponse.",
    noImprovements: "Aucun point d’amélioration précis n’a été relevé dans cette réponse.",
    relevance: "Pertinence",
    evidence: "Éléments à l'Appui",
    organization: "Organisation",
    competency: "Compétence Professionnelle",
    results: "Preuve de Résultats",
    presence: "Présence Professionnelle",
    grammar: "Grammaire",
    vocabulary: "Vocabulaire",
    clarity: "Clarté",
    tone: "Ton Professionnel",
    fluency: "Fluidité",
    insufficient: "Il n'y avait pas assez d'anglais parlé dans cette réponse pour évaluer de façon fiable les dimensions linguistiques.",
    locked: "Exemple de rapport. Pour voir le rapport complet de l’entrevue et le guide d’étude, inscrivez-vous à un compte payant.",
    report: "Télécharger le Rapport d'Entrevue",
    guide: "Télécharger le Guide d'Étude",
    paidOnly: "Disponible avec un compte payant",
    home: "Retourner à l'accueil du compte",
  },
};

function scoreColor(v) {
  if (v >= 75) return "var(--score-good)";
  if (v >= 50) return "var(--score-average)";
  return "var(--score-bad)";
}

function ScoreBar({ label, value }) {
  const isNA = value === null || value === undefined;
  const pct = isNA ? 0 : Number(value);
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[color:var(--text-secondary)] uppercase tracking-widest text-[10px]">{label}</span>
        <span className="mono font-medium">{isNA ? "—" : value}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-[color:var(--border-stone)] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: isNA ? "0%" : `${pct}%`, background: isNA ? "transparent" : scoreColor(pct) }} />
      </div>
    </div>
  );
}

export default function TrialReport() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/sessions/${sessionId}/trial-report-preview`)
      .then(({ data: payload }) => {
        if (alive) setData(payload || {});
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => { alive = false; };
  }, [sessionId]);

  const lang = data?.interface_language || user?.preferred_language || data?.language || "en";
  const t = COPY[lang] || COPY.en;

  if (failed) {
    return <div className="min-h-screen flex items-center justify-center"><button type="button" onClick={() => navigate("/")} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-3">{t.home}</button></div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-[color:var(--text-secondary)]">Loading…</div>;
  }

  const q = data.questions?.[0];
  const a = data.answers?.[0];
  const ev = a?.evaluation || null;
  const pt = String(lang).toLowerCase().startsWith("pt") ? ev?.pt_br : null;
  const strengths = (pt?.strengths ?? ev?.strengths) || [];
  const improvements = (pt?.improvements ?? ev?.improvements) || [];

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="trial-report-preview">
      <header className="border-b border-[color:var(--border-stone)] bg-white/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/logo.png" alt="My English Coach Interview Simulator logo" className="h-[63px] w-[63px] object-contain" />
            <div className="min-w-0">
              <div className="font-semibold tracking-tight truncate">My English Coach Interview Simulator</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--brand)] mt-1">{t.eyebrow}</div>
            </div>
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 py-8 sm:py-12">
        <section className="rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 sm:p-9">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
          <p className="mt-4 max-w-3xl text-[color:var(--text-secondary)] leading-relaxed">{t.intro}</p>
        </section>

        {q && (
          <section className="mt-8 rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 sm:p-8" data-testid="trial-question-feedback">
            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{t.question} 1{q.focus ? ` · ${q.focus}` : ""}</div>
                <div className="mt-2 text-lg font-semibold tracking-tight leading-snug">{q.text}</div>
              </div>
              {ev && (
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{t.overall}</div>
                  <div className="mono text-3xl font-semibold" style={{ color: scoreColor(ev.overall) }}>{ev.overall}</div>
                </div>
              )}
            </div>

            {ev && (
              <>
                <div className="mt-7">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.performance}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <ScoreBar label={t.relevance} value={ev.relevance} />
                    <ScoreBar label={t.evidence} value={ev.supporting_evidence} />
                    <ScoreBar label={t.organization} value={ev.organization} />
                    <ScoreBar label={t.competency} value={ev.professional_competency} />
                    <ScoreBar label={t.results} value={ev.evidence_of_results} />
                    <ScoreBar label={t.presence} value={ev.professional_presence} />
                  </div>
                </div>

                <div className="mt-7">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.english}</div>
                  {ev.english_insufficient ? (
                    <div className="text-sm italic text-[color:var(--text-secondary)] rounded-2xl bg-[color:var(--bg-surface-hover)] px-5 py-4 border border-dashed border-[color:var(--border-stone)]">{t.insufficient}</div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                      <ScoreBar label={t.grammar} value={ev.grammar} />
                      <ScoreBar label={t.vocabulary} value={ev.vocabulary} />
                      <ScoreBar label={t.clarity} value={ev.clarity} />
                      <ScoreBar label={t.tone} value={ev.professional_tone} />
                      <ScoreBar label={t.fluency} value={ev.fluency} />
                    </div>
                  )}
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.transcript}</div>
                    <div className="rounded-2xl bg-[color:var(--bg-surface-hover)] p-5 text-sm leading-relaxed">{a?.transcript || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.suggested}</div>
                    <div className="rounded-2xl bg-[color:var(--brand)]/5 border border-[color:var(--brand)]/20 p-5 text-sm leading-relaxed">{ev.suggested_answer || "—"}</div>
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-[color:var(--brand)] mb-3"><Star weight="duotone" /> {t.strengths}</div>
                    {strengths.length > 0 ? (
                      <ul className="space-y-2">{strengths.map((s, i) => <li key={i} className="flex gap-3 text-sm"><CheckCircle size={16} weight="fill" className="mt-0.5 text-[color:var(--brand)] shrink-0" /><span>{s}</span></li>)}</ul>
                    ) : (
                      <p className="text-sm italic text-[color:var(--text-secondary)]">{t.noStrengths}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-[color:var(--score-bad)] mb-3"><Warning weight="duotone" /> {t.improve}</div>
                    {improvements.length > 0 ? (
                      <ul className="space-y-2">{improvements.map((s, i) => <li key={i} className="flex gap-3 text-sm"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[color:var(--score-bad)] shrink-0" /><span>{s}</span></li>)}</ul>
                    ) : (
                      <p className="text-sm italic text-[color:var(--text-secondary)]">{t.noImprovements}</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        <section className="mt-8 rounded-3xl border border-[color:var(--brand)]/25 bg-white p-6 sm:p-8" data-testid="trial-report-paywall">
          <div className="flex items-start gap-3">
            <LockSimple size={24} weight="duotone" className="text-[color:var(--brand)] shrink-0 mt-0.5" />
            <p className="font-semibold text-lg leading-relaxed">{t.locked}</p>
          </div>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <LockedDownload icon={ChartBar} title={t.report} subtitle={t.paidOnly} />
            <LockedDownload icon={BookOpen} title={t.guide} subtitle={t.paidOnly} />
          </div>

          <button type="button" onClick={() => navigate("/", { replace: true })} className="mt-7 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-3 text-sm font-medium">{t.home}</button>
        </section>
      </main>
    </div>
  );
}

function LockedDownload({ icon: Icon, title, subtitle }) {
  return (
    <div className="rounded-2xl border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] p-5 opacity-60" aria-disabled="true">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-[color:var(--border-stone)] flex items-center justify-center shrink-0"><Icon weight="duotone" size={22} /></div>
        <div className="font-semibold">{title}</div>
      </div>
      <button type="button" disabled className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-300 text-slate-600 px-5 py-3 text-sm font-medium cursor-not-allowed"><LockSimple size={16} /><DownloadSimple size={16} />{subtitle}</button>
    </div>
  );
}
